import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  Component,
} from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useHotkeys } from "react-hotkeys-hook";
import axios from "axios";
import useAutoSave from "../hooks/useAutoSave";
import AutoSaveIndicator from "./AutoSaveIndicator";
import { getApiUrl } from "../utils/apiConfig";

class SubtitleEditorErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo,
    });

    console.error("SubtitleEditor Error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            padding: "2rem",
            textAlign: "center",
            backgroundColor: "#fff5f5",
            border: "1px solid #fed7d7",
            borderRadius: "8px",
            margin: "1rem",
          }}
        >
          <h2 style={{ color: "#c53030", marginBottom: "1rem" }}>
            ⚠️ Something went wrong
          </h2>
          <p style={{ marginBottom: "1rem" }}>
            The subtitle editor encountered an error. Please refresh the page
            and try again.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: "0.5rem 1rem",
              backgroundColor: "#3182ce",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
            }}
          >
            Refresh Page
          </button>
          {process.env.NODE_ENV === "development" && (
            <details style={{ marginTop: "1rem", textAlign: "left" }}>
              <summary>Error Details (Development)</summary>
              <pre
                style={{
                  backgroundColor: "#f7fafc",
                  padding: "1rem",
                  borderRadius: "4px",
                  overflow: "auto",
                  fontSize: "0.875rem",
                }}
              >
                {this.state.error && this.state.error.toString()}
                {this.state.errorInfo && this.state.errorInfo.componentStack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

const SubtitleEditor = ({ user }) => {
  const { projectId } = useParams();
  const navigate = useNavigate();

  const DEFAULT_CPS_LIMIT = 20;
  const DEFAULT_CPL_LIMIT = 42;
  const DEBOUNCE_DELAY = 300;
  const SEEK_FRAMES_24FPS = 0.083;
  const SEEK_100MS = 0.1;
  const SEEK_5S = 5;

  const [project, setProject] = useState(null);
  const [entries, setEntries] = useState([]);
  const [currentEntryIndex, setCurrentEntryIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [videoPath, setVideoPath] = useState("");
  const [videoError, setVideoError] = useState("");
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoLoading, setVideoLoading] = useState(false);
  const [lastVttContent, setLastVttContent] = useState("");
  const [vttUpdateTimeout, setVttUpdateTimeout] = useState(null);

  const [textareaFocused, setTextareaFocused] = useState(false);
  const [showCompletionModal, setShowCompletionModal] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [showErrorReport, setShowErrorReport] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [sendingReport, setSendingReport] = useState(false);
  const [reportSuccess, setReportSuccess] = useState(false);


  const [cpsLimit, setCpsLimit] = useState(DEFAULT_CPS_LIMIT);
  const [cplLimit, setCplLimit] = useState(DEFAULT_CPL_LIMIT);


  const [showTranslationOverlay, setShowTranslationOverlay] = useState(true);
  const [webVttUrl, setWebVttUrl] = useState(null);

  const videoRef = useRef(null);
  const inputRefs = useRef({});
  const textareaFocusedRef = useRef(false);
  const saveOnEntryChangeRef = useRef(null);

  /** Quebra a linha antes do travessão (—, – ou -) na exibição de legendas (formato diálogo). */
  const breakLineAtDash = useCallback((text) => {
    if (!text || typeof text !== "string") return text;
    return text
      .replace(/\s—/g, "\n— ")
      .replace(/\s–/g, "\n– ")
      .replace(/\s-/g, "\n- ");
  }, []);

  const generateWebVTT = useCallback(() => {
    console.log("🔍 generateWebVTT called, entries:", entries.length);
    const vttContent = ["WEBVTT\n"];

    entries.forEach((entry, index) => {
      if (entry.target_text && entry.target_text.trim()) {
        console.log(
          `🔍 Entry ${index + 1}: "${entry.target_text.trim()}" (${entry.start_time
          } --> ${entry.end_time})`
        );

        const startTime = entry.start_time.replace(",", ".");
        const endTime = entry.end_time.replace(",", ".");

        vttContent.push(`${index + 1}`);
        vttContent.push(`${startTime} --> ${endTime}`);
        vttContent.push(`${breakLineAtDash(entry.target_text.trim())}`);
        vttContent.push("");
      }
    });

    const result = vttContent.join("\n");
    console.log("🔍 Final VTT content:", result);
    return result;
  }, [entries, breakLineAtDash]);


  useEffect(() => {
    console.log(
      "🔍 useEffect updateWebVTTOverlay called, showTranslationOverlay:",
      showTranslationOverlay
    );


    if (vttUpdateTimeout) {
      clearTimeout(vttUpdateTimeout);
    }



    const shouldDebounce = entries.length > 0 && showTranslationOverlay;
    const delay = shouldDebounce ? 300 : 0;

    const timeoutId = setTimeout(() => {
      updateWebVTT();
    }, delay);

    setVttUpdateTimeout(timeoutId);


    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [showTranslationOverlay, entries]);


  const updateWebVTT = () => {
    if (!showTranslationOverlay) {
      if (webVttUrl) {
        URL.revokeObjectURL(webVttUrl);
        setWebVttUrl(null);
      }
      return;
    }


    console.log("🔍 generateWebVTT called, entries:", entries.length);
    const vttContent = ["WEBVTT\n"];

    entries.forEach((entry, index) => {
      if (entry.target_text && entry.target_text.trim()) {
        console.log(
          `🔍 Entry ${index + 1}: "${entry.target_text.trim()}" (${entry.start_time
          } --> ${entry.end_time})`
        );

        const startTime = entry.start_time.replace(",", ".");
        const endTime = entry.end_time.replace(",", ".");

        vttContent.push(`${index + 1}`);
        vttContent.push(`${startTime} --> ${endTime}`);
        vttContent.push(`${breakLineAtDash(entry.target_text.trim())}`);
        vttContent.push("");
      }
    });

    const result = vttContent.join("\n");
    console.log("🔍 Final VTT content:", result);


    if (result !== lastVttContent || !webVttUrl) {
      console.log("🔍 VTT content changed or URL missing, creating new URL");
      setLastVttContent(result);


      if (webVttUrl) {
        URL.revokeObjectURL(webVttUrl);
      }

      const blob = new Blob([result], { type: "text/vtt" });
      const url = URL.createObjectURL(blob);
      console.log("🔍 Created WebVTT URL:", url);
      setWebVttUrl(url);
    } else {
      console.log(
        "🔍 VTT content unchanged and URL exists, skipping recreation"
      );
    }


    if (videoRef.current && videoRef.current.textTracks) {
      console.log("🔍 Updating subtitle track without reloading video");


      for (let i = 0; i < videoRef.current.textTracks.length; i++) {
        const track = videoRef.current.textTracks[i];
        if (track.label === "Current Translation") {

          if (showTranslationOverlay) {
            track.mode = "showing";
            console.log("🔍 Track activated:", track.label);


            setTimeout(() => {
              if (track.mode === "showing") {
                track.dispatchEvent(new Event("change"));
                console.log("🔍 Track change event dispatched");
              }
            }, 100);
          } else {
            track.mode = "disabled";
            console.log("🔍 Track disabled:", track.label);
          }
        } else {
          track.mode = "disabled";
        }
      }
    }
  };


  useEffect(() => {
    const handleVideoLoad = () => {
      if (
        videoRef.current &&
        videoRef.current.textTracks &&
        showTranslationOverlay
      ) {
        console.log("🔍 Video loaded, forcing track activation");
        setTimeout(() => {
          for (let i = 0; i < videoRef.current.textTracks.length; i++) {
            const track = videoRef.current.textTracks[i];
            if (track.label === "Current Translation") {
              track.mode = "showing";
              console.log("🔍 Track activated on video load:", track.label);
            } else {
              track.mode = "disabled";
            }
          }
        }, 200);
      }
    };

    if (videoRef.current) {
      videoRef.current.addEventListener("loadedmetadata", handleVideoLoad);
      videoRef.current.addEventListener("canplay", handleVideoLoad);
    }

    return () => {
      if (videoRef.current) {
        videoRef.current.removeEventListener("loadedmetadata", handleVideoLoad);
        videoRef.current.removeEventListener("canplay", handleVideoLoad);
      }
    };
  }, [showTranslationOverlay]);


  useEffect(() => {
    if (!showTranslationOverlay) return;

    const forceTrackActivation = () => {
      if (videoRef.current && videoRef.current.textTracks) {
        console.log("🔍 Aggressive track activation attempt");
        for (let i = 0; i < videoRef.current.textTracks.length; i++) {
          const track = videoRef.current.textTracks[i];
          if (track.label === "Current Translation") {
            track.mode = "showing";
            console.log("🔍 Track aggressively activated:", track.label);
          } else {
            track.mode = "disabled";
          }
        }
      }
    };


    const timeouts = [100, 300, 500, 1000, 2000];
    timeouts.forEach((delay) => {
      setTimeout(forceTrackActivation, delay);
    });


    const handleAnyVideoEvent = () => {
      setTimeout(forceTrackActivation, 50);
    };

    if (videoRef.current) {
      videoRef.current.addEventListener("loadstart", handleAnyVideoEvent);
      videoRef.current.addEventListener("loadeddata", handleAnyVideoEvent);
      videoRef.current.addEventListener("loadedmetadata", handleAnyVideoEvent);
      videoRef.current.addEventListener("canplay", handleAnyVideoEvent);
      videoRef.current.addEventListener("canplaythrough", handleAnyVideoEvent);
      videoRef.current.addEventListener("play", handleAnyVideoEvent);
      videoRef.current.addEventListener("playing", handleAnyVideoEvent);
    }

    return () => {
      if (videoRef.current) {
        videoRef.current.removeEventListener("loadstart", handleAnyVideoEvent);
        videoRef.current.removeEventListener("loadeddata", handleAnyVideoEvent);
        videoRef.current.removeEventListener(
          "loadedmetadata",
          handleAnyVideoEvent
        );
        videoRef.current.removeEventListener("canplay", handleAnyVideoEvent);
        videoRef.current.removeEventListener(
          "canplaythrough",
          handleAnyVideoEvent
        );
        videoRef.current.removeEventListener("play", handleAnyVideoEvent);
        videoRef.current.removeEventListener("playing", handleAnyVideoEvent);
      }
    };
  }, [showTranslationOverlay, webVttUrl]);


  const calculateCPL = (text) => {
    if (!text) return 0;

    const lines = text.split("\n");
    return Math.max(...lines.map((line) => line.length));
  };


  const calculateCPS = useCallback((text, startTime, endTime) => {
    if (!text || !startTime || !endTime) return 0;

    const start = parseTimeString(startTime);
    const end = parseTimeString(endTime);
    const duration = end - start;

    if (duration <= 0) return 0;

    return text.length / duration;
  }, []);


  const checkCPSLimit = useCallback(
    (text, startTime, endTime) => {
      const cps = calculateCPS(text, startTime, endTime);
      return {
        cps,
        exceeds: cps > cpsLimit,
        limit: cpsLimit,
      };
    },
    [cpsLimit, calculateCPS]
  );


  const checkCPLLimit = useCallback(
    (text) => {
      if (!text) {
        return {
          cpl: 0,
          exceeds: false,
          limit: cplLimit,
          lineCount: 0,
        };
      }

      const lines = text.split("\n").filter((line) => line.trim().length > 0);
      const lineCount = lines.length;
      const maxLineLength = Math.max(...lines.map((line) => line.length), 0);


      const exceeds = lineCount > 2 || maxLineLength > cplLimit;

      return {
        cpl: maxLineLength,
        exceeds,
        limit: cplLimit,
        lineCount,
      };
    },
    [cplLimit]
  );


  const validateFormatting = useCallback((sourceText, targetText) => {
    if (!sourceText || !targetText)
      return { isValid: true, missingFormats: [] };


    const extractFormatting = (text) => {
      if (!text) return [];

      const patterns = [];


      const htmlTags = text.match(/<[^>]+>/g) || [];
      patterns.push(...htmlTags);


      const formatCodes = text.match(/\{[^}]+\}/g) || [];
      patterns.push(...formatCodes);

      return patterns;
    };

    const sourceFormats = extractFormatting(sourceText);
    const targetFormats = extractFormatting(targetText);

    const missingFormats = sourceFormats.filter(
      (format) => !targetFormats.includes(format)
    );

    return {
      isValid: missingFormats.length === 0,
      missingFormats,
      sourceFormats,
      targetFormats,
    };
  }, []);


  const getValidationStatus = useCallback(
    (entry) => {
      const cpsCheck = checkCPSLimit(
        entry.target_text,
        entry.start_time,
        entry.end_time
      );
      const cplCheck = checkCPLLimit(entry.target_text);
      const formattingCheck = validateFormatting(
        entry.source_text,
        entry.target_text
      );

      return {
        cps: cpsCheck,
        cpl: cplCheck,
        formatting: formattingCheck,
        isValid:
          !cpsCheck.exceeds && !cplCheck.exceeds && formattingCheck.isValid,
      };
    },
    [checkCPSLimit, checkCPLLimit, validateFormatting]
  );

  const fetchProjectData = useCallback(async () => {
    try {
      setLoading(true);
      const apiUrl = await getApiUrl(`/api/projects/${projectId}`);
      const projectResponse = await axios.get(apiUrl);


      const { entries, ...projectData } = projectResponse.data;


      const cleanedEntries =
        entries?.map((entry) => {
          if (entry.target_text) {
            const trimmedText = entry.target_text.trim();


            const placeholderPatterns = [
              /^translate.*$/i,
              /^enter.*$/i,
              /^type.*$/i,
              /^placeholder.*$/i,
              /^example.*$/i,
              /^test.*$/i,
              /^demo.*$/i,
              /^sample.*$/i,
              /^click.*$/i,
              /^fill.*$/i,
              /^input.*$/i,
            ];


            if (
              placeholderPatterns.some((pattern) => pattern.test(trimmedText))
            ) {
              return { ...entry, target_text: "" };
            }


            if (
              entry.source_text &&
              trimmedText.toLowerCase() === entry.source_text.toLowerCase()
            ) {
              return { ...entry, target_text: "" };
            }
          }
          return entry;
        }) || [];

      setProject(projectData);
      setEntries(cleanedEntries);


      setCpsLimit(projectData.cps_limit || DEFAULT_CPS_LIMIT);
      setCplLimit(projectData.cpl_limit || DEFAULT_CPL_LIMIT);


      const apiBaseUrl = await getApiUrl("");


      const isNasProject =
        projectData.use_nas || projectData.video_path.startsWith("/Home/");

      if (isNasProject) {

        const nasPath = `${apiBaseUrl}/api/nas-files${projectData.video_path}`;
        console.log("🎬 Setting NAS video path:", nasPath);
        setVideoPath(nasPath);
      } else {

        const localPath = `${apiBaseUrl}/uploads/${projectData.video_path.replace(
          "/uploads/",
          ""
        )}`;
        console.log("📁 Setting local video path:", localPath);
        setVideoPath(localPath);
      }
    } catch (error) {
      setError("Failed to load project data");
      console.error("Error fetching project data:", error);
    } finally {
      setLoading(false);
    }
  }, [projectId, DEFAULT_CPS_LIMIT, DEFAULT_CPL_LIMIT]);

  useEffect(() => {
    fetchProjectData();


    const handleGlobalKeyDown = (e) => {
      if (textareaFocusedRef.current) {

        const videoShortcuts = [
          " ",
          "ArrowLeft",
          "ArrowRight",
          "ArrowUp",
          "ArrowDown",
          "Home",
          "End",
        ];
        if (videoShortcuts.includes(e.key)) {
          e.stopPropagation();
        }
      }
    };

    document.addEventListener("keydown", handleGlobalKeyDown);

    return () => {
      document.removeEventListener("keydown", handleGlobalKeyDown);
    };
  }, [fetchProjectData]);


  useEffect(() => {
    return () => {
      if (webVttUrl) {
        URL.revokeObjectURL(webVttUrl);
      }
    };
  }, [webVttUrl]);


  useEffect(() => {
    const currentInputRefs = inputRefs.current;
    return () => {

      Object.values(currentInputRefs).forEach((ref) => {
        if (ref?.timeout) {
          clearTimeout(ref.timeout);
        }
      });
    };
  }, []);


  const checkCompletionStatus = useCallback(() => {
    if (!entries || entries.length === 0) {
      return;
    }


    const translatedEntries = entries.filter((entry) => {
      const targetText = entry.target_text;
      if (!targetText || !targetText.trim()) return false;


      const trimmedText = targetText.trim();
      if (trimmedText.length === 0) return false;


      if (entry.source_text && trimmedText === entry.source_text.trim())
        return false;


      if (trimmedText.length < 2) return false;

      return true;
    });

    const allTranslated = translatedEntries.length === entries.length;

    if (allTranslated) {

      const entriesWithErrors = entries.filter((entry) => {
        if (!entry.target_text) return false;
        const validation = getValidationStatus(entry);
        return !validation.isValid;
      });

      if (entriesWithErrors.length > 0) {
        return;
      }
    } else {

    }
  }, [entries, getValidationStatus]);


  const markEntriesAsTranslated = useCallback(async () => {
    if (!entries || entries.length === 0) return;

    try {

      const entriesToMark = entries.filter((entry) => {
        const targetText = entry.target_text;
        if (!targetText || !targetText.trim()) return false;
        if (entry.source_text && targetText.trim() === entry.source_text.trim())
          return false;
        if (targetText.trim().length < 2) return false;


        return !entry.is_translated;
      });

      if (entriesToMark.length === 0) return;


      const markPromises = entriesToMark.map(async (entry) => {
        const apiUrl = await getApiUrl(
          `/api/projects/${projectId}/entries/${entry.id}`
        );
        return axios.put(apiUrl, {
          target_text: entry.target_text,
          is_translated: true,
        });
      });

      await Promise.all(markPromises);


      setEntries((prev) =>
        prev.map((entry) => {
          if (entriesToMark.some((e) => e.id === entry.id)) {
            return { ...entry, is_translated: true };
          }
          return entry;
        })
      );

      console.log(`Marked ${entriesToMark.length} entries as translated`);
    } catch (error) {
      console.error("Error marking entries as translated:", error);
      setError("Failed to mark entries as translated");
    }
  }, [entries, projectId]);


  const saveAllProgress = useCallback(async () => {
    if (!entries || entries.length === 0) return;

    try {

      const entriesToSave = entries.filter((entry) => {
        const targetText = entry.target_text;
        return targetText && targetText.trim().length > 0;
      });

      if (entriesToSave.length === 0) {
        console.log("No entries with text to save");
        return;
      }

      console.log(`Saving ${entriesToSave.length} entries with text`);


      const savePromises = entriesToSave.map(async (entry) => {
        const apiUrl = await getApiUrl(
          `/api/projects/${projectId}/entries/${entry.id}`
        );
        return axios.put(apiUrl, {
          target_text: entry.target_text,
          is_translated: true,
        });
      });

      await Promise.all(savePromises);


      setEntries((prev) =>
        prev.map((entry) => {
          if (entriesToSave.some((e) => e.id === entry.id)) {
            return { ...entry, is_translated: true };
          }
          return entry;
        })
      );

      console.log(`Successfully saved ${entriesToSave.length} entries`);
    } catch (error) {
      console.error("Error saving progress:", error);
      setError("Failed to save progress");
      throw error;
    }
  }, [entries, projectId]);

  const saveEntry = useCallback(
    async (entryId, targetText) => {
      try {
        setSaving(true);


        const entry = entries.find((e) => e.id === entryId);
        const shouldMarkAsTranslated =
          entry &&
          targetText &&
          targetText.trim() &&
          targetText.trim().length >= 2 &&
          (!entry.source_text ||
            targetText.trim() !== entry.source_text.trim());

        const apiUrl = await getApiUrl(
          `/api/projects/${projectId}/entries/${entryId}`
        );
        await axios.put(apiUrl, {
          target_text: targetText,
          ...(shouldMarkAsTranslated && { is_translated: true }),
        });


        setEntries((prev) =>
          prev.map((entry) =>
            entry.id === entryId
              ? {
                ...entry,
                target_text: targetText,
                ...(shouldMarkAsTranslated && { is_translated: true }),
              }
              : entry
          )
        );
      } catch (error) {
        console.error("Error saving entry:", error);


        let errorMessage = "Failed to save entry";

        if (error.response) {

          const status = error.response.status;
          const data = error.response.data;

          if (status === 400 && data?.error === "CPS_CPL_VIOLATION") {
            errorMessage = data.message || "Validation error: " + (data.type === "CPS"
              ? `CPS limit exceeded (${data.current?.toFixed(1)} > ${data.limit})`
              : `CPL limit exceeded (${data.current} > ${data.limit})`);
          } else if (status === 404) {
            errorMessage = "Entry or project not found";
          } else if (status === 403) {
            errorMessage = "You don't have permission to save this entry";
          } else if (data?.message) {
            errorMessage = data.message;
          } else {
            errorMessage = `Failed to save entry (${status})`;
          }
        } else if (error.request) {

          errorMessage = "Network error: Could not reach server. Please check your connection.";
        } else {

          errorMessage = error.message || "Failed to save entry";
        }

        setError(errorMessage);


        setTimeout(() => {
          setError("");
        }, 5000);
      } finally {
        setSaving(false);
      }
    },
    [projectId, setError, entries]
  );

  const handleInputChange = useCallback(
    (entryId, value) => {

      setEntries((prev) =>
        prev.map((entry) =>
          entry.id === entryId ? { ...entry, target_text: value } : entry
        )
      );


      if (showTranslationOverlay) {

        if (!inputRefs.current[entryId]) {
          inputRefs.current[entryId] = {};
        }


        if (inputRefs.current[entryId].toggleTimeout) {
          clearTimeout(inputRefs.current[entryId].toggleTimeout);
        }


        inputRefs.current[entryId].toggleTimeout = setTimeout(() => {
          console.log(
            "🔍 Toggling subtitle overlay to force refresh (debounced)"
          );
          setShowTranslationOverlay(false);
          setTimeout(() => {
            setShowTranslationOverlay(true);
          }, 50);
        }, 800);
      }




      if (inputRefs.current[entryId]?.timeout) {
        clearTimeout(inputRefs.current[entryId].timeout);
      }
      if (inputRefs.current[entryId]?.saveTimeout) {
        clearTimeout(inputRefs.current[entryId].saveTimeout);
      }


      const timeoutId = setTimeout(() => {

        checkCompletionStatus();
      }, DEBOUNCE_DELAY);


      if (!inputRefs.current[entryId]) {
        inputRefs.current[entryId] = {};
      }
      inputRefs.current[entryId].timeout = timeoutId;


      const entrySnapshot = entries.find((e) => e.id === entryId);
      const originalText = entrySnapshot?.source_text || "";
      const validation = entrySnapshot
        ? getValidationStatus({ ...entrySnapshot, target_text: value })
        : { isValid: true };
      const savePayload = {
        draftText: value || "",
        originalText,
        hasErrors: validation ? !validation.isValid : false,
      };
      const saveTimeoutId = setTimeout(() => {

        if (saveOnEntryChangeRef.current) {
          saveOnEntryChangeRef.current(savePayload);
        }

        saveEntry(entryId, value);
      }, 600);
      inputRefs.current[entryId].saveTimeout = saveTimeoutId;
    },
    [
      DEBOUNCE_DELAY,
      checkCompletionStatus,
      entries,
      getValidationStatus,
      showTranslationOverlay,
      saveEntry,
    ]
  );


  useEffect(() => {
    if (!loading && entries && entries.length > 0) {
      checkCompletionStatus();
    }
  }, [loading, entries, checkCompletionStatus]);


  const currentEntry = entries[currentEntryIndex];
  const currentDraftPayload = React.useMemo(() => {
    if (!currentEntry) return null;
    const validation = getValidationStatus(currentEntry);
    return {
      draftText: currentEntry.target_text || "",
      originalText: currentEntry.source_text || "",
      hasErrors: !validation.isValid,
      errorDetails: !validation.isValid
        ? "Validation issues present (CPS/CPL/formatting)"
        : undefined,
    };
  }, [currentEntry, getValidationStatus]);

  const {
    lastSaved,
    isSaving: autoSaving,
    isOnline,
    hasOfflineDrafts,
    isSyncInProgress,
    manualSave,
    saveStatus,
    saveOnEntryChange,
  } = useAutoSave(
    projectId,
    currentEntry?.sequence_number,
    currentDraftPayload,
    {
      onSaveSuccess: () => { },
      onSaveError: (error) => {
        console.error("Auto-save failed:", error);
        setError(`Auto-save failed: ${error.message}`);
      },
      onOfflineStorage: () => { },
    }
  );


  useEffect(() => {
    saveOnEntryChangeRef.current = saveOnEntryChange;
  }, [saveOnEntryChange]);

  const seekToTime = (timeString) => {
    if (videoRef.current) {
      const time = parseTimeString(timeString);
      videoRef.current.currentTime = time;
    }
  };

  const parseTimeString = (timeString) => {
    const match = timeString.match(/(\d{2}):(\d{2}):(\d{2}),(\d{3})/);
    if (!match) return 0;

    const [, hours, minutes, seconds, milliseconds] = match;
    return (
      parseInt(hours) * 3600 +
      parseInt(minutes) * 60 +
      parseInt(seconds) +
      parseInt(milliseconds) / 1000
    );
  };

  const formatTime = (timeString) => {
    const match = timeString.match(/(\d{2}):(\d{2}):(\d{2}),(\d{3})/);
    if (!match) return timeString;

    const [, hours, minutes, seconds] = match;
    return `${hours}:${minutes}:${seconds}`;
  };


  const seekBackward = (seconds) => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.max(
        0,
        videoRef.current.currentTime - seconds
      );
    }
  };

  const seekForward = (seconds) => {
    if (videoRef.current) {
      videoRef.current.currentTime = Math.min(
        videoRef.current.duration,
        videoRef.current.currentTime + seconds
      );
    }
  };

  const handleSave = async () => {
    try {

      await saveAllProgress();
      setError("");

      console.log("Progress saved successfully");
    } catch (error) {
      setError("Failed to save progress");
      console.error("Error saving project:", error);
    }
  };

  const handleComplete = async () => {
    try {

      await markEntriesAsTranslated();
      const completeUrl = await getApiUrl(
        `/api/projects/${projectId}/complete`
      );
      await axios.post(completeUrl);
      navigate("/dashboard");
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.details) {
        setError(`Cannot complete project: ${error.response.data.details}`);
      } else if (
        error.response?.status === 400 &&
        error.response?.data?.validationErrors
      ) {
        const errorCount = error.response.data.validationErrors.length;
        setError(
          `Cannot complete project: ${errorCount} validation error(s) found. Please fix all CPS/CPL issues before completing.`
        );
      } else {
        setError("Failed to complete project");
      }
      console.error("Error completing project:", error);
    }
  };

  const handleSendErrorReport = async () => {
    if (!errorMessage.trim()) {
      setError("Please enter a message describing the issue");
      return;
    }

    try {
      setSendingReport(true);
      const apiUrl = await getApiUrl("/api/report-error");
      await axios.post(apiUrl, {
        message: errorMessage.trim(),
        user_name: user.name,
        user_email: user.email,
        user_type: user.user_type,
        project_name: project?.name,
        project_id: projectId,
      });

      setReportSuccess(true);
      setErrorMessage("");
      setTimeout(() => {
        setShowErrorReport(false);
        setReportSuccess(false);
      }, 2000);
    } catch (error) {
      setError("Failed to send error report");
      console.error("Error sending report:", error);
    } finally {
      setSendingReport(false);
    }
  };

  const downloadSRT = async () => {
    try {
      setDownloading(true);

      let srtContent = "";
      let translatedCount = 0;
      let untranslatedCount = 0;
      let entriesWithErrors = 0;

      entries.forEach((entry, index) => {
        const textToUse =
          entry.target_text && entry.target_text.trim()
            ? entry.target_text.trim()
            : entry.source_text;


        if (entry.target_text && entry.target_text.trim()) {
          translatedCount++;
        } else {
          untranslatedCount++;
        }


        if (entry.target_text) {
          const validation = getValidationStatus(entry);
          if (!validation.isValid) {
            entriesWithErrors++;
          }
        }


        srtContent += `${index + 1}\n`;
        srtContent += `${entry.start_time} --> ${entry.end_time}\n`;
        srtContent += `${textToUse}\n\n`;
      });


      const blob = new Blob([srtContent], { type: "text/plain;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${project?.name || "subtitle"}_translated.srt`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);



      if (entriesWithErrors > 0) {
        console.log(
          `SRT downloaded successfully! ${translatedCount} entries translated, ${untranslatedCount} entries using source text. Note: ${entriesWithErrors} entries have validation errors (CPS/CPL) but were included in the download.`
        );

        setError(
          `✅ SRT downloaded successfully. Note: ${entriesWithErrors} entries have validation errors but were included in the download.`
        );
      } else {

        setError(
          `✅ SRT downloaded successfully! ${translatedCount} entries translated, ${untranslatedCount} entries using source text.`
        );
        console.log(
          `SRT downloaded successfully! ${translatedCount} entries translated, ${untranslatedCount} entries using source text.`
        );
      }


      if (untranslatedCount > 0 && entriesWithErrors === 0) {
        console.log(
          `Note: ${untranslatedCount} entries still use original source text (not yet translated).`
        );
      }
    } catch (error) {
      setError("Failed to generate SRT file");
      console.error("Error generating SRT:", error);
    } finally {
      setDownloading(false);
    }
  };


  useHotkeys(
    "up",
    () => {
      setCurrentEntryIndex((prev) => Math.max(0, prev - 1));
    },
    { enableOnFormTags: true }
  );

  useHotkeys(
    "down",
    () => {
      setCurrentEntryIndex((prev) => Math.min(entries.length - 1, prev + 1));
    },
    { enableOnFormTags: true }
  );

  useHotkeys(
    "space",
    (e) => {

      if (textareaFocused) {
        return;
      }
      e.preventDefault();
      if (videoRef.current) {
        if (videoRef.current.paused) {
          videoRef.current.play();
        } else {
          videoRef.current.pause();
        }
      }
    },
    { enableOnFormTags: true }
  );

  useHotkeys(
    "ctrl+s",
    (e) => {
      e.preventDefault();

      entries.forEach((entry) => {
        if (inputRefs.current[entry.id]?.timeout) {
          clearTimeout(inputRefs.current[entry.id].timeout);
          saveEntry(entry.id, entry.target_text);
        }
      });
    },
    { enableOnFormTags: true }
  );

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "2rem" }}>
        Loading subtitle editor...
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div
          className={error.startsWith("✅") ? "alert alert-success" : "alert alert-danger"}
          style={{
            margin: "1rem",
            marginBottom: "0.5rem",
            padding: "0.75rem 1rem",
            borderRadius: "4px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: error.startsWith("✅") ? "#d4edda" : "#f8d7da",
            border: `1px solid ${error.startsWith("✅") ? "#c3e6cb" : "#f5c6cb"}`,
            color: error.startsWith("✅") ? "#155724" : "#721c24",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <span style={{ flex: 1 }}>{error}</span>
          <button
            onClick={() => setError("")}
            style={{
              background: "none",
              border: "none",
              fontSize: "1.5rem",
              cursor: "pointer",
              color: error.startsWith("✅") ? "#155724" : "#721c24",
              padding: "0 0.5rem",
              marginLeft: "1rem",
              lineHeight: "1",
              fontWeight: "bold",
            }}
            title="Close message"
          >
            ×
          </button>
        </div>
      )}

      <div className="editor-container">

        {/* Completion Modal */}
        {showCompletionModal && (
          <div
            className="modal-overlay"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <div
              className="modal-content"
              style={{
                backgroundColor: "white",
                padding: "2rem",
                borderRadius: "8px",
                maxWidth: "500px",
                textAlign: "center",
                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
              }}
            >
              <h3 style={{ color: "#28a745", marginBottom: "1rem" }}>
                🎉 Translation Complete!
              </h3>
              <p style={{ marginBottom: "1.5rem", fontSize: "1.1rem" }}>
                All translations have been completed successfully!
              </p>
              <div
                style={{ display: "flex", gap: "1rem", justifyContent: "center" }}
              >
                <button
                  className="btn btn-success"
                  onClick={() => {
                    setShowCompletionModal(false);
                    handleComplete();
                  }}
                >
                  Complete Project
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowCompletionModal(false);
                  }}
                >
                  Continue Editing
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Subtitle Editor Panel */}
        <div className="editor-panel">
          <div className="editor-header">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "0.5rem",
              }}
            >
              <h2 className="editor-title">{project?.name}</h2>
              <div
                style={{
                  display: "flex",
                  gap: "0.5rem",
                  alignItems: "center",
                  flexWrap: "wrap",
                }}
              >
                {/* Auto-save indicator */}
                <AutoSaveIndicator
                  saveStatus={saveStatus}
                  lastSaved={lastSaved}
                  isOnline={isOnline}
                  hasOfflineDrafts={hasOfflineDrafts}
                  isSyncInProgress={isSyncInProgress}
                  onManualSave={manualSave}
                  className="header-integration"
                />

                {(saving || autoSaving) && (
                  <span style={{ fontSize: "0.875rem", color: "#6c757d" }}>
                    {autoSaving ? "Auto-saving..." : "Saving..."}
                  </span>
                )}

                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => navigate("/dashboard")}
                  title="Go back to dashboard"
                >
                  🏠 Home
                </button>
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={downloadSRT}
                  disabled={downloading}
                  title="Download SRT with current translations (including auto-saved content)"
                >
                  {downloading ? "⏳" : "📄"} Download SRT
                </button>
                <button
                  className="btn btn-outline-warning btn-sm"
                  onClick={() => setShowErrorReport(true)}
                  title="Report an issue to admin"
                >
                  🚩 Report Issue
                </button>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={handleSave}
                  title="Save current progress"
                >
                  💾 Save
                </button>
                {entries.filter((e) => !e.target_text || !e.target_text.trim())
                  .length === 0 && (
                    <button
                      className="btn btn-sm btn-success"
                      onClick={handleComplete}
                      title="Deliver completed project and move to proofreading"
                    >
                      ✅ Deliver
                    </button>
                  )}
              </div>
            </div>
            <div
              style={{
                fontSize: "0.875rem",
                color: "#6c757d",
                marginTop: "0.5rem",
              }}
            >
              <div style={{ marginBottom: "0.25rem" }}>
                <strong>Your language:</strong> {project?.target_language}
              </div>
              <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                <span>
                  <strong>CPS:</strong> {cpsLimit}
                </span>
                <span>
                  <strong>CPL:</strong> {cplLimit}
                </span>
                <span>
                  <strong>Missing lines:</strong>{" "}
                  {
                    entries.filter((e) => !e.target_text || !e.target_text.trim())
                      .length
                  }
                </span>
              </div>
            </div>
            {videoError && (
              <div
                className="alert alert-warning"
                style={{ marginTop: "0.5rem" }}
              >
                ⚠️ {videoError} - Video playback may not be available
              </div>
            )}
          </div>

          <div className="subtitle-list">
            {entries.map((entry, index) => {
              const validation = getValidationStatus(entry);
              return (
                <div
                  key={entry.id}
                  className={`subtitle-entry ${index === currentEntryIndex ? "active" : ""
                    } ${entry.target_text && !validation.isValid
                      ? "validation-error"
                      : ""
                    }`}
                  onClick={(e) => {

                    if (e.target.tagName !== "TEXTAREA") {
                      setCurrentEntryIndex(index);
                    }
                  }}
                >
                  <div className="subtitle-header">
                    <span className="subtitle-number">
                      #{entry.sequence_number}
                    </span>
                    <span
                      className="subtitle-time"
                      style={{ cursor: "pointer" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        seekToTime(entry.start_time);
                      }}
                    >
                      {formatTime(entry.start_time)} →{" "}
                      {formatTime(entry.end_time)}
                    </span>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      style={{
                        marginLeft: "0.5rem",
                        padding: "0.25rem 0.5rem",
                        fontSize: "0.8rem",
                        minWidth: "60px",
                      }}
                      onClick={(e) => {
                        e.stopPropagation();

                        seekToTime(entry.start_time);

                        if (videoRef.current) {
                          if (videoRef.current.paused) {
                            videoRef.current.play();
                          } else {
                            videoRef.current.pause();
                          }
                        }
                      }}
                      title={`${isVideoPlaying ? "Pause" : "Play"
                        } from ${formatTime(entry.start_time)}`}
                    >
                      {isVideoPlaying ? "⏸️ Pause" : "▶️ Play"}
                    </button>
                    {/* CPS, CPL, and Formatting indicators */}
                    <div className="validation-indicators">
                      <span
                        className={`cps-indicator ${validation.cps.exceeds ? "error" : "ok"
                          }`}
                        title={`CPS: ${validation.cps.cps.toFixed(1)}/${validation.cps.limit
                          }`}
                      >
                        CPS: {validation.cps.cps.toFixed(1)}
                      </span>
                      <span
                        className={`cpl-indicator ${validation.cpl.exceeds ? "error" : "ok"
                          }`}
                        title={`CPL: ${validation.cpl.cpl}/${validation.cpl.limit} | Lines: ${validation.cpl.lineCount || 0}/2`}
                      >
                        CPL: {validation.cpl.cpl}
                        {validation.cpl.lineCount > 0 && ` (${validation.cpl.lineCount} lines)`}
                      </span>
                      <span
                        className={`formatting-indicator ${!validation.formatting.isValid ? "error" : "ok"
                          }`}
                        title={`Formatting: ${validation.formatting.isValid
                          ? "Valid"
                          : "Missing: " +
                          validation.formatting.missingFormats.join(", ")
                          }`}
                      >
                        FMT: {validation.formatting.isValid ? "✓" : "✗"}
                      </span>
                    </div>
                  </div>

                  <div className="subtitle-content">
                    <div className="subtitle-text source" style={{ whiteSpace: "pre-line" }}>
                      {breakLineAtDash(entry.source_text)}
                    </div>

                    <textarea
                      className={`subtitle-input ${entry.target_text && !validation.isValid
                        ? "validation-error"
                        : ""
                        }`}
                      value={entry.target_text || ""}
                      onChange={(e) =>
                        handleInputChange(entry.id, e.target.value)
                      }
                      placeholder={`Translate to ${project?.target_language}...`}
                      disabled={project?.status === "completed"}
                      rows={2}
                      wrap="soft"
                      style={{
                        border:
                          entry.target_text && !validation.isValid
                            ? "2px solid #dc3545"
                            : "1px solid #ced4da",
                        backgroundColor:
                          entry.target_text && !validation.isValid
                            ? "#fff5f5"
                            : "white",
                        outline: textareaFocused ? "2px solid #007bff" : "none",
                        resize: "vertical",
                      }}
                      onFocus={() => {
                        setTextareaFocused(true);
                        textareaFocusedRef.current = true;
                      }}
                      onBlur={() => {
                        setTextareaFocused(false);
                        textareaFocusedRef.current = false;
                      }}
                      onKeyDown={(e) => {

                        e.stopPropagation();
                      }}
                      onKeyUp={(e) => {

                        e.stopPropagation();
                      }}
                      onKeyPress={(e) => {

                        e.stopPropagation();
                      }}
                    />

                    {/* Validation error messages */}
                    {entry.target_text && !validation.isValid && (
                      <div className="validation-errors">
                        {validation.cps.exceeds && (
                          <div className="error-message">
                            ⚠️ CPS too high: {validation.cps.cps.toFixed(1)}/
                            {validation.cps.limit}
                          </div>
                        )}
                        {validation.cpl.exceeds && (
                          <div className="error-message">
                            ⚠️ {validation.cpl.lineCount > 2
                              ? `Too many lines: ${validation.cpl.lineCount}/2 (max 2 lines allowed)`
                              : `CPL too high: ${validation.cpl.cpl}/${validation.cpl.limit} (max ${validation.cpl.limit} characters per line)`}
                          </div>
                        )}
                        {!validation.formatting.isValid && (
                          <div className="error-message">
                            ⚠️ Missing formatting:{" "}
                            {validation.formatting.missingFormats.join(", ")}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Video Player Panel */}
        <div className="video-panel">
          {textareaFocused && (
            <div
              style={{
                backgroundColor: "#fff3cd",
                border: "1px solid #ffeaa7",
                borderRadius: "4px",
                padding: "8px 12px",
                marginBottom: "10px",
                fontSize: "0.875rem",
                color: "#856404",
                textAlign: "center",
              }}
            >
              ⚠️ Video shortcuts disabled while typing
            </div>
          )}
          <div className="video-container">
            {videoPath ? (
              <div className="d-flex flex-column gap-2">
                {videoLoading && (
                  <div className="video-loading-overlay">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading video...</span>
                    </div>
                    <div className="mt-2">
                      <small className="text-muted">
                        Loading video metadata... This may take a moment for large
                        files.
                      </small>
                    </div>
                  </div>
                )}
                <>
                  <video
                    ref={videoRef}
                    className="video-player"
                    controls
                    src={videoPath}
                    preload="metadata"
                    playsInline
                    onLoadStart={() => {
                      setVideoLoading(true);
                      setVideoError("");
                    }}
                    onLoadedMetadata={() => {
                      setVideoLoaded(true);
                      setVideoLoading(false);
                    }}
                    onCanPlay={() => {
                      setVideoLoading(false);
                    }}
                    onError={() => {
                      setVideoError("Failed to load video");
                      setVideoLoading(false);
                    }}
                    onTimeUpdate={() => {
                      if (!videoRef.current) return;

                      const currentTime = videoRef.current.currentTime;


                      const currentEntry = entries.find((entry) => {
                        const start = parseTimeString(entry.start_time);
                        const end = parseTimeString(entry.end_time);
                        return currentTime >= start && currentTime <= end;
                      });

                      if (currentEntry) {
                        const entryIndex = entries.findIndex(
                          (e) => e.id === currentEntry.id
                        );
                        setCurrentEntryIndex(entryIndex);
                      }
                    }}
                    onPlay={() => setIsVideoPlaying(true)}
                    onPause={() => setIsVideoPlaying(false)}
                    onEnded={() => setIsVideoPlaying(false)}
                    onKeyDown={(e) => {

                      if (textareaFocused) {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                  >
                    {showTranslationOverlay && webVttUrl && (
                      <track
                        kind="subtitles"
                        src={webVttUrl}
                        default={true}
                        label="Current Translation"
                        onLoad={() => console.log("🔍 Track loaded successfully")}
                        onError={(e) => console.log("🔍 Track error:", e)}
                        ref={(el) => {
                          if (el) {
                            console.log("🔍 Track element rendered:", el);
                          }
                        }}
                      />
                    )}
                  </video>

                  {/* Video Navigation Controls */}
                </>
                <div
                  className="video-controls"
                  style={{
                    display: "flex",
                    gap: "0.5rem",
                    marginTop: "0.5rem",
                    justifyContent: "center",
                    flexWrap: "wrap",
                    padding: "10px",
                    backgroundColor: "#f8f9fa",
                    borderRadius: "8px",
                    border: "1px solid #dee2e6",
                    height: "50px",
                  }}
                >
                  {/* WebVTT Overlay Toggle */}
                  <button
                    className={`btn btn-sm ${showTranslationOverlay
                      ? "btn-success"
                      : "btn-outline-secondary"
                      }`}
                    onClick={() => {
                      console.log(
                        "🔍 Show Subs button clicked, current state:",
                        showTranslationOverlay
                      );
                      setShowTranslationOverlay(!showTranslationOverlay);
                    }}
                    title={
                      showTranslationOverlay
                        ? "Hide translation overlay"
                        : "Show translation overlay"
                    }
                    style={{
                      fontSize: "14px",
                      fontWeight: "bold",
                      padding: "6px 12px",
                      minWidth: "100px",
                      backgroundColor: showTranslationOverlay
                        ? "#28a745"
                        : "#007bff",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {showTranslationOverlay ? "🎭 Hide Subs" : "🎭 Show Subs"}
                  </button>

                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => seekBackward(SEEK_100MS)}
                    title="Back 100ms"
                  >
                    -100ms
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => seekBackward(SEEK_FRAMES_24FPS)}
                    title="Back 2 frames (at 24fps)"
                  >
                    -2f
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => seekBackward(SEEK_5S)}
                    title="Back 5 seconds"
                  >
                    -5s
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => seekForward(SEEK_100MS)}
                    title="Forward 100ms"
                  >
                    +100ms
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => seekForward(SEEK_FRAMES_24FPS)}
                    title="Forward 2 frames (at 24fps)"
                  >
                    +2f
                  </button>
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => seekForward(SEEK_5S)}
                    title="Forward 5 seconds"
                  >
                    +5s
                  </button>
                </div>
              </div>
            ) : (
              <div
                className="video-placeholder"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "300px",
                  backgroundColor: "#f8f9fa",
                  border: "2px dashed #dee2e6",
                  borderRadius: "8px",
                  color: "#6c757d",
                }}
              >
                <div style={{ textAlign: "center" }}>
                  <div style={{ fontSize: "2rem", marginBottom: "0.5rem" }}>
                    🎬
                  </div>
                  <div>Video not available</div>
                  <div style={{ fontSize: "0.875rem" }}>
                    Check with admin if video file is uploaded
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Error Reporting Modal */}
        {showErrorReport && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <div
              style={{
                backgroundColor: "white",
                padding: "2rem",
                borderRadius: "8px",
                maxWidth: "500px",
                width: "90%",
                maxHeight: "80vh",
                overflow: "auto",
              }}
            >
              <h3 style={{ marginBottom: "1rem", color: "#dc3545" }}>
                🚩 Report Issue to Admin
              </h3>

              {reportSuccess ? (
                <div style={{ textAlign: "center", padding: "1rem" }}>
                  <div
                    style={{
                      color: "#28a745",
                      fontSize: "2rem",
                      marginBottom: "1rem",
                    }}
                  >
                    ✅
                  </div>
                  <p style={{ color: "#28a745", fontWeight: "bold" }}>
                    Issue reported successfully! Admin will be notified.
                  </p>
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: "1rem" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "0.5rem",
                        fontWeight: "bold",
                      }}
                    >
                      Describe the issue:
                    </label>
                    <textarea
                      value={errorMessage}
                      onChange={(e) => setErrorMessage(e.target.value)}
                      placeholder="Please describe the issue you're experiencing..."
                      style={{
                        width: "100%",
                        minHeight: "120px",
                        padding: "0.75rem",
                        border: "1px solid #ced4da",
                        borderRadius: "4px",
                        resize: "vertical",
                      }}
                    />
                  </div>

                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "1rem",
                      borderRadius: "4px",
                      marginBottom: "1rem",
                      fontSize: "0.875rem",
                      color: "#6c757d",
                    }}
                  >
                    <strong>Report will include:</strong>
                    <ul style={{ margin: "0.5rem 0 0 1rem" }}>
                      <li>Your name: {user.name}</li>
                      <li>Your email: {user.email}</li>
                      <li>Your role: {user.user_type}</li>
                      <li>Project: {project?.name}</li>
                      <li>Your message: {errorMessage || "(empty)"}</li>
                    </ul>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: "0.5rem",
                      justifyContent: "flex-end",
                    }}
                  >
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowErrorReport(false);
                        setErrorMessage("");
                        setError("");
                      }}
                      disabled={sendingReport}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-warning"
                      onClick={handleSendErrorReport}
                      disabled={sendingReport || !errorMessage.trim()}
                    >
                      {sendingReport ? "⏳ Sending..." : "📧 Send Report"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


const SubtitleEditorWithErrorBoundary = (props) => (
  <SubtitleEditorErrorBoundary>
    <SubtitleEditor {...props} />
  </SubtitleEditorErrorBoundary>
);

export default SubtitleEditorWithErrorBoundary;
